/**
 * SATCHEL — About us page behaviour.
 *
 * Used by the "About us — Hero" and "About us — Image with text" sections. Two jobs, both
 * progressive enhancements over markup that already works on its own:
 *
 * 1. Text reveal. The rows render their text fully visible. This script hides each piece
 *    of text (eyebrow, heading, every paragraph, each collapsible row, the button) only
 *    while it is still below the viewport, and reveals it once it scrolls in. So if this
 *    file never loads nothing is hidden, and text already on screen does not flash out
 *    and back in. The stagger and the motion are pure CSS in the section.
 *
 * 2. Video autoplay. Videos render with native controls and preload="none". This script
 *    swaps the controls for a pause button and plays the video, muted, only while it is
 *    on screen. It never autoplays for visitors with reduced motion or data saver turned
 *    on. On mobile data in Egypt a video that downloads off screen is a real cost.
 *
 * Every section on the page can output the script tag, so it guards against running twice.
 */
(() => {
  if (window.satchelAboutUsLoaded) return;
  window.satchelAboutUsLoaded = true;

  if (!('IntersectionObserver' in window)) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = navigator.connection?.saveData === true;

  /* ---------------------------------------------------------------------------------- */
  /* Text reveal                                                                        */
  /* ---------------------------------------------------------------------------------- */

  const PENDING_CLASS = 'satchel-about-reveal--pending';
  const ITEM_REVEALED_CLASS = 'satchel-about-reveal-item--revealed';
  const ORDER_PROPERTY = '--satchel-about-reveal-order';

  // Must match the pieces hidden by the CSS in the section. Paragraphs come from a rich
  // text setting and cannot carry a class, so they are matched by position instead.
  const ITEM_SELECTOR = '.satchel-about-media__reveal, .satchel-about-media__text > *';

  const byDocumentOrder = (a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);

  // Each piece is observed on its own, so on a phone every paragraph animates as it
  // reaches the screen rather than all at once while most of them are still out of sight.
  // Pieces that arrive in the same frame are numbered top to bottom, and the CSS turns
  // that number into a short stagger.
  const revealObserver = new IntersectionObserver(
    (entries) => {
      const arriving = entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => entry.target)
        .sort(byDocumentOrder);

      arriving.forEach((item, index) => {
        item.style.setProperty(ORDER_PROPERTY, index);
        item.classList.add(ITEM_REVEALED_CLASS);
        revealObserver.unobserve(item);
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  const setupReveal = (root) => {
    if (reducedMotion.matches) return;

    root.querySelectorAll('[data-satchel-about-reveal]').forEach((content) => {
      if (content.dataset.satchelAboutReveal === 'ready') return;
      content.dataset.satchelAboutReveal = 'ready';

      const items = [...content.querySelectorAll(ITEM_SELECTOR)];
      const waiting = [];

      // Anything already on screen is marked revealed before the content is hidden, so it
      // never flashes out and back in. Only what is still below the screen waits.
      for (const item of items) {
        if (item.getBoundingClientRect().top < window.innerHeight) {
          item.classList.add(ITEM_REVEALED_CLASS);
        } else {
          waiting.push(item);
        }
      }

      if (waiting.length === 0) return;

      content.classList.add(PENDING_CLASS);
      waiting.forEach((item) => revealObserver.observe(item));
    });
  };

  /* ---------------------------------------------------------------------------------- */
  /* Video autoplay                                                                     */
  /* ---------------------------------------------------------------------------------- */

  const PLAYING_CLASS = 'satchel-about-video--playing';

  // A quarter of the video on screen is enough to count as being watched. Scrolling it
  // away pauses it, but does not count as the visitor pausing it, so it resumes on return.
  const videoObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target.querySelector('video');

        if (entry.isIntersecting) {
          if (entry.target.dataset.pausedByVisitor !== 'true') {
            // play() rejects if the browser blocks autoplay. The pause button is still
            // there, so the visitor can start it themselves.
            video.play().catch(() => {});
          }
        } else if (!video.paused) {
          video.pause();
        }
      }
    },
    { threshold: 0.25 }
  );

  const setupVideos = (root) => {
    if (reducedMotion.matches || saveData) return;

    root.querySelectorAll('[data-satchel-about-video]').forEach((wrapper) => {
      if (wrapper.dataset.satchelAboutVideo === 'ready') return;

      const video = wrapper.querySelector('video');
      const toggle = wrapper.querySelector('.satchel-about-video__toggle');
      if (!video || !toggle) return;

      wrapper.dataset.satchelAboutVideo = 'ready';

      const sync = () => {
        const playing = !video.paused;
        wrapper.classList.toggle(PLAYING_CLASS, playing);
        toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
      };

      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);

      toggle.addEventListener('click', () => {
        if (video.paused) {
          delete wrapper.dataset.pausedByVisitor;
          video.play().catch(() => {});
        } else {
          wrapper.dataset.pausedByVisitor = 'true';
          video.pause();
        }
      });

      video.controls = false;
      toggle.hidden = false;
      sync();
      videoObserver.observe(wrapper);
    });
  };

  /* ---------------------------------------------------------------------------------- */

  const setup = (root) => {
    setupReveal(root);
    setupVideos(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setup(document), { once: true });
  } else {
    setup(document);
  }

  // The theme editor replaces a section's HTML whenever a setting changes.
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
